import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Eye, EyeOff, MailCheck, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email, origin: window.location.origin });
        setSentTo(email);
        setLoading(false);
        return;
      }
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center mb-8">
          <Link to="/" data-testid="admin-logo-home" aria-label="Back to home">
            <img src="/upwardcast-logo-transparent.png" alt="UpwardCast" className="h-9 w-auto hover:opacity-80 transition-opacity" />
          </Link>
        </div>

        {mode === "forgot" && sentTo ? (
          <div className="bg-white border border-[#0A0A0A] hard-shadow p-8 text-center" data-testid="forgot-sent">
            <MailCheck className="w-9 h-9 text-[#6D28B5] mx-auto mb-3" />
            <h1 className="text-xl font-bold tracking-tight mb-2">Check your inbox</h1>
            <p className="text-sm text-[#4B5563]">If an account exists for <span className="text-[#0A0A0A] font-medium">{sentTo}</span>, we just sent a link to reset your password. It's good for one hour.</p>
            <button onClick={() => { setMode("login"); setSentTo(""); }} data-testid="back-to-login-btn"
              className="mt-6 text-sm font-semibold text-[#6D28B5] hover:text-[#4A1A7A] inline-flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#0A0A0A] hard-shadow p-8 space-y-5">
            <div>
              <h1 className="text-xl font-bold tracking-tight">{mode === "forgot" ? "Reset your password" : "Admin sign in"}</h1>
              <p className="text-sm text-[#4B5563] mt-1">{mode === "forgot" ? "Enter your email and we'll send you a reset link." : "Manage your webinars and campaigns."}</p>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <input
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full border border-[#0A0A0A] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]"
            />
            {mode === "login" && (
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full border border-[#0A0A0A] px-4 py-3 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  data-testid="login-password-toggle"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0A0A0A]"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-6 py-3 font-semibold transition-colors flex items-center justify-center"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "forgot" ? "Send reset link" : "Sign in"}
            </button>
            <div className="text-center">
              {mode === "login" ? (
                <button type="button" onClick={() => { setMode("forgot"); setError(""); }} data-testid="forgot-password-link" className="text-sm font-medium text-[#6D28B5] hover:text-[#4A1A7A]">
                  Forgot password?
                </button>
              ) : (
                <button type="button" onClick={() => { setMode("login"); setError(""); }} data-testid="back-to-login-link" className="text-sm font-medium text-[#4B5563] hover:text-[#0A0A0A] inline-flex items-center gap-1.5">
                  <ArrowLeft className="w-4 h-4" /> Back to sign in
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
